/**
 * Tiny, dependency-free markdown → safe HTML converter.
 *
 * Covers the blog-post subset we actually use: ATX headings, fenced + inline
 * code, bold/italic emphasis, links, ordered + unordered lists, blockquotes,
 * horizontal rules, paragraphs. Anything HTML-y in the input gets escaped
 * first — no `dangerouslySetInnerHTML` foot-gun — so even if a future post
 * pastes raw `<script>` we render it as text.
 */

const ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ESCAPE[c]);
}

function renderInline(text: string): string {
  // Escape first, THEN apply inline syntax so we never lose track of `<` `>` in code.
  let out = escapeHtml(text);

  // Inline code first — its contents shouldn't get further markdown applied.
  out = out.replace(/`([^`]+)`/g, (_, code) => `<code>${code}</code>`);

  // Bold (**), italic (*) — bold processed before italic to avoid * conflicts.
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");

  // Links: [label](url). Only http(s)/relative.
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
    const safe = /^(https?:\/\/|\/|mailto:)/.test(url) ? url : "#";
    const external = safe.startsWith("http");
    const rel = external ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `<a href="${safe}"${rel}>${label}</a>`;
  });

  return out;
}

export function renderMarkdown(input: string): string {
  if (!input) return "";
  const lines = input.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];

  let i = 0;
  let inList: "ul" | "ol" | null = null;
  let paragraph: string[] = [];

  function flushParagraph() {
    if (paragraph.length) {
      out.push(`<p>${renderInline(paragraph.join(" "))}</p>`);
      paragraph = [];
    }
  }
  function closeList() {
    if (inList) {
      out.push(`</${inList}>`);
      inList = null;
    }
  }

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block ```
    if (/^```/.test(line)) {
      flushParagraph();
      closeList();
      const lang = line.replace(/^```/, "").trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++; // consume closing fence
      const langClass = lang ? ` class="lang-${lang}"` : "";
      out.push(`<pre><code${langClass}>${escapeHtml(buf.join("\n"))}</code></pre>`);
      continue;
    }

    // Blank line → close paragraph + list
    if (/^\s*$/.test(line)) {
      flushParagraph();
      closeList();
      i++;
      continue;
    }

    // Horizontal rule
    if (/^---+\s*$/.test(line) || /^\*\*\*+\s*$/.test(line)) {
      flushParagraph();
      closeList();
      out.push("<hr/>");
      i++;
      continue;
    }

    // Standalone image on its own line: ![alt](url). Rendered as a
    // figure so blog covers + inline art get semantic markup + lazy
    // loading. Only http(s)/relative srcs (no data:/js: injection).
    const img = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/.exec(line);
    if (img) {
      flushParagraph();
      closeList();
      const alt = escapeHtml(img[1]);
      const src = /^(https?:\/\/|\/)/.test(img[2]) ? escapeHtml(img[2]) : "";
      if (src) {
        out.push(
          `<figure><img src="${src}" alt="${alt}" loading="lazy" decoding="async"/>` +
            (alt ? `<figcaption>${alt}</figcaption>` : "") +
            "</figure>",
        );
      }
      i++;
      continue;
    }

    // Standalone video embed: @video(url). Self-hosted mp4 only
    // (same-origin /public/blog-media/... or an http(s) URL). Renders
    // a native <video> element — no third-party iframe, no XSS surface.
    const vid = /^@video\(([^)]+)\)\s*$/.exec(line);
    if (vid) {
      flushParagraph();
      closeList();
      const src = /^(https?:\/\/|\/)/.test(vid[1]) ? escapeHtml(vid[1]) : "";
      if (src) {
        out.push(
          `<figure><video src="${src}" controls preload="metadata" ` +
            `playsinline style="width:100%;border-radius:8px"></video></figure>`,
        );
      }
      i++;
      continue;
    }

    // Heading
    const heading = /^(#{1,6})\s+(.+?)\s*$/.exec(line);
    if (heading) {
      flushParagraph();
      closeList();
      const level = heading[1].length;
      out.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      i++;
      continue;
    }

    // Blockquote
    if (/^>\s+/.test(line)) {
      flushParagraph();
      closeList();
      const buf: string[] = [];
      while (i < lines.length && /^>\s+/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s+/, ""));
        i++;
      }
      out.push(`<blockquote>${renderInline(buf.join(" "))}</blockquote>`);
      continue;
    }

    // Ordered list item
    const ol = /^(\d+)\.\s+(.+)$/.exec(line);
    if (ol) {
      flushParagraph();
      if (inList !== "ol") {
        closeList();
        out.push("<ol>");
        inList = "ol";
      }
      out.push(`<li>${renderInline(ol[2])}</li>`);
      i++;
      continue;
    }

    // Unordered list item
    const ul = /^[-*+]\s+(.+)$/.exec(line);
    if (ul) {
      flushParagraph();
      if (inList !== "ul") {
        closeList();
        out.push("<ul>");
        inList = "ul";
      }
      out.push(`<li>${renderInline(ul[1])}</li>`);
      i++;
      continue;
    }

    // Paragraph accumulation
    closeList();
    paragraph.push(line.trim());
    i++;
  }

  flushParagraph();
  closeList();
  return out.join("\n");
}
